import { Provider } from '../provider'
import { cleanGraphQLResponse } from '@/utils'
import {
  Cart,
  Checkout,
  Collection,
  CollectionAndProduct,
  CollectionAndProducts,
  NullOrType,
  Page,
  Product,
  ProductVariant,
} from '../../types'
import { ShopifyGraphQLClient } from './graphql'
import {
  CheckoutAttributesUpdateInput,
  CheckoutDiscountCodeApplyInput,
  CheckoutDiscountCodeRemoveInput,
  CheckoutEmailUpdateInput,
  CheckoutLineItemsAddInput,
  CheckoutLineItemsRemoveInput,
  CheckoutLineItemsUpdateInput,
  CompleteCheckoutInput,
  CreateCartInput,
  CartLinesAddInput,
  CartLinesUpdateInput,
  CartBuyerIdentityUpdateInput,
  CartDiscountCodesUpdateInput,
  CreateCheckoutInput,
  GetCollectionParam,
  ProductFilter,
  UpdateCheckoutShippingAddressInput,
  UpdateCheckoutShippingLineInput,
} from '../type'
import {
  SHOP_DOMAIN,
  SHOP_CHECKOUT_DOMAIN,
  PRODUCT_TAG_DISCOUNT_DEFAULT,
} from '@/utils'
import { URLRedirect } from '@/types/onlineStore'
import {
  CustomerAccessTokenCreateInput,
  CustomerActivateInput,
  CustomerCreateInput,
  CustomerResetInput,
} from '@/provider/type'

export class ShopifyProvider extends Provider {
  client: ShopifyGraphQLClient
  constructor() {
    super()
    this.client = new ShopifyGraphQLClient({
      storefrontAccessToken: import.meta.env
        .VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN as string,
      domain: (import.meta.env.VITE_SHOPIFY_CHECKOUT_DOMAIN ||
        import.meta.env.VITE_SHOPIFY_DOMAIN) as string,
      apiVersion: import.meta.env.VITE_SHOPIFY_API_VERSION as string,
    })
  }

  // product zone
  async getProductById(id: string): Promise<Product> {
    const productData = await this.client.getProductById(id)
    return this.transformProduct(productData.product)
  }

  async getPageByHandle(handle: string): Promise<Page> {
    const pageData = await this.client.getPageByHandle(handle)
    return pageData.page
  }

  async getArticleBlogByHandle(
    blogHandle: string,
    articleHandle: string
  ): Promise<any> {
    const blogData = await this.client.getArticleBlogByHandle(
      blogHandle,
      articleHandle
    )
    return blogData
  }

  async getListArticleBlogByHandle(param: {
    handle: string
    numberOfArticles?: number
    after?: string
    before?: string
  }): Promise<any> {
    const listArticleBlogData = await this.client.getListArticleBlogByHandle(
      param
    )
    if (listArticleBlogData) {
      return listArticleBlogData.blogByHandle
    }
    return null
  }

  async getProductByHandle(handle: string): Promise<Product> {
    const productData = await this.client.getProductByHandle(handle)
    return this.transformProduct(productData.product)
  }

  // collection zone
  async getCollectionByHandle(
    param: GetCollectionParam
  ): Promise<NullOrType<Collection>> {
    if (param.filters) {
      param.filters = this.transformProductFilterParam(param.filters)
    }
    const collectionData = await this.client.getCollectionWithProductsByHandle(
      param
    )
    return this.transformCollection(collectionData.collection)
  }

  async getCollectionByHandleAndProductByIds(
    param: GetCollectionParam
  ): Promise<CollectionAndProducts> {
    if (param.filters) {
      param.filters = this.transformProductFilterParam(param.filters)
    }
    const collectionData =
      await this.client.getCollectionWithProductsByHandleAndProductByIds(param)
    const products = collectionData.nodes.filter((product: any) => product)
    return {
      collection: this.transformCollection(collectionData.collection),
      products: this.transformProducts(products),
    }
  }

  async getCollectionByHandleAndProductByHandle(
    param: GetCollectionParam
  ): Promise<CollectionAndProduct> {
    if (param.filters) {
      param.filters = this.transformProductFilterParam(param.filters)
    }
    const collectionData =
      await this.client.getCollectionWithProductsByHandleAndProductByHandle(
        param
      )
    return {
      collection: this.transformCollection(collectionData.collection),
      product: this.transformProduct(collectionData.product),
    }
  }

  // cart zone
  async createCart(input: CreateCartInput): Promise<Cart> {
    const data = await this.client.createCart(input)
    return this.transformCart(data?.cartCreate?.cart)
  }

  async getCart(cartId: string): Promise<Cart> {
    const data = await this.client.getCart(cartId)
    return this.transformCart(data?.cart)
  }

  async cartLinesAdd(input: CartLinesAddInput): Promise<Cart> {
    const data = await this.client.cartLinesAdd(input)
    return this.transformCart(data?.cartLinesAdd?.cart)
  }

  async cartLinesRemove(input: CartLinesRemoveInput): Promise<Cart> {
    const data = await this.client.cartLinesRemove(input)
    return this.transformCart(data?.cartLinesRemove?.cart)
  }

  async cartLinesUpdate(input: CartLinesUpdateInput): Promise<Cart> {
    const data = await this.client.cartLinesUpdate(input)
    return this.transformCart(data?.cartLinesUpdate?.cart)
  }

  async cartBuyerIdentityUpdate(
    input: CartBuyerIdentityUpdateInput
  ): Promise<Cart> {
    const data = await this.client.cartBuyerIdentityUpdate(
      input.buyerIdentity,
      input.cartId
    )
    return this.transformCart(data?.cartLinesUpdate?.cart)
  }

  async cartDiscountCodesUpdate(
    input: CartDiscountCodesUpdateInput
  ): Promise<Cart> {
    const data = await this.client.cartDiscountCodesUpdate(
      input.cartId,
      input.discountCodes
    )
    return this.transformCart(data?.cartDiscountCodesUpdate?.cart)
  }

  async cartAttributesUpdate(input: any): Promise<Cart> {
    await this.client.cartAttributesUpdate(input.attributes, input.cartId)
  }

  // checkout zone
  async createCheckout(input: CreateCheckoutInput): Promise<Checkout> {
    const data = await this.client.createCheckout(input)
    return this.transformCheckout(data.checkoutCreate.checkout)
  }

  async getCheckout(checkoutId: string): Promise<Checkout> {
    const checkoutData = await this.client.getCheckout(checkoutId)
    return this.transformCheckout(checkoutData.node)
  }

  async checkoutLineItemsAdd(
    input: CheckoutLineItemsAddInput
  ): Promise<Checkout> {
    const data = await this.client.checkoutLineItemsAdd(input)
    return this.transformCheckout(data.checkoutLineItemsAdd.checkout)
  }

  async checkoutLineItemsRemove(
    input: CheckoutLineItemsRemoveInput
  ): Promise<Checkout> {
    const data = await this.client.checkoutLineItemsRemove(input)
    return this.transformCheckout(data.checkoutLineItemsRemove.checkout)
  }

  async checkoutLineItemsUpdate(
    input: CheckoutLineItemsUpdateInput
  ): Promise<Checkout> {
    const data = await this.client.checkoutLineItemsUpdate(input)
    return this.transformCheckout(data.checkoutLineItemsUpdate.checkout)
  }

  async checkoutAttributesUpdate(
    input: CheckoutAttributesUpdateInput
  ): Promise<Checkout> {
    const data = await this.client.checkoutAttributesUpdate(input)
    return this.transformCheckout(data.checkoutAttributesUpdateV2.checkout)
  }

  async checkoutDiscountCodeApply(
    input: CheckoutDiscountCodeApplyInput
  ): Promise<{ checkout: Checkout; checkoutErrors: any }> {
    const data = await this.client.checkoutDiscountCodeApply(input)
    return {
      checkout: this.transformCheckout(
        data.checkoutDiscountCodeApplyV2.checkout
      ),
      checkoutErrors: data.checkoutDiscountCodeApplyV2.checkoutUserErrors,
    }
  }

  async checkoutDiscountCodeRemove(
    input: CheckoutDiscountCodeRemoveInput
  ): Promise<Checkout> {
    const data = await this.client.checkoutDiscountCodeRemove(input)
    return this.transformCheckout(data.checkoutDiscountCodeRemove.checkout)
  }

  async checkoutEmailUpdate(
    input: CheckoutEmailUpdateInput
  ): Promise<Checkout> {
    const data = await this.client.checkoutEmailUpdate(input)
    return this.transformCheckout(data.checkoutEmailUpdateV2.checkout)
  }

  async updateCheckoutShippingAddress(
    input: UpdateCheckoutShippingAddressInput
  ): Promise<Checkout> {
    return await this.client.updateCheckoutShippingAddress(
      input.checkoutId,
      input.shippingAddress
    )
  }

  async updateCheckoutShippingLine(
    input: UpdateCheckoutShippingLineInput
  ): Promise<Checkout> {
    return await this.client.updateCheckoutShippingLine(
      input.checkoutId,
      input.shippingRateHandle
    )
  }

  async completeCheckout(input: CompleteCheckoutInput): Promise<Checkout> {
    return await this.client.completeCheckout(input.checkoutId, input.payment)
  }

  async completeFreeCheckout(checkoutId: string): Promise<Checkout> {
    return await this.client.completeFreeCheckout(checkoutId)
  }

  // Customer zone
  async createCustomer(input: CustomerCreateInput): Promise<any> {
    return await this.client.createCustomer(input)
  }

  async createCustomerAccessToken(
    input: CustomerAccessTokenCreateInput
  ): Promise<any> {
    return await this.client.createCustomerAccessToken(input)
  }

  async getCustomer(customerAccessToken: string): Promise<any> {
    return await this.client.getCustomer(customerAccessToken)
  }

  async recoverCustomer(email: string): Promise<any> {
    return await this.client.recoverCustomer(email)
  }

  async resetCustomer(id: string, input: CustomerResetInput): Promise<any> {
    return await this.client.resetCustomer(id, input)
  }

  async activeCustomer(id: string, input: CustomerActivateInput): Promise<any> {
    return await this.client.activeCustomer(id, input)
  }

  async createCustomerAddress(
    address: MailingAddressInput,
    customerAccessToken: string
  ): Promise<any> {
    return await this.client.createCustomerAddress(address, customerAccessToken)
  }
  async updateCustomerAddress(
    address: MailingAddressInput,
    customerAccessToken: string,
    id: string
  ): Promise<any> {
    return await this.client.updateCustomerAddress(
      address,
      customerAccessToken,
      id
    )
  }
  async getLocalization(): Promise<any> {
    return await this.client.getLocalization()
  }

  async getProductRecommendations(id: string): Promise<any> {
    const data = await this.client.getProductRecommendations(id)
    return data.productRecommendations?.map((product: any) => {
      return this.transformProduct(cleanGraphQLResponse(product))
    })
  }

  // Online Store zone
  async getURLRedirects(path: string): Promise<URLRedirect[]> {
    if (path) {
      path = path.toLowerCase()
    } else {
      return []
    }
    const response = await this.client.getURLRedirects(path)
    return response['urlRedirects']
  }

  // transformers
  transformProductVariant(variant: any): ProductVariant {
    if (!variant) return variant
    if (variant.image) {
      variant.image = {
        ...variant.image,
        src: variant.image.url,
        alt: variant.image.altText || variant.title,
      }
    }

    if (variant.priceV2 && !variant.price) {
      variant.price = +variant.priceV2.amount || 0
    }
    if (variant.compareAtPriceV2 && !variant.compareAtPrice) {
      variant.compareAtPrice = +variant.compareAtPriceV2.amount || 0
    }
    return variant
  }

  transformProductVariantDiscountDefault(
    variant: any,
    discountPercentage: number
  ): ProductVariant {
    if (!variant) return variant
    if (variant.image) {
      variant.image = {
        ...variant.image,
        src: variant.image.url,
        alt: variant.image.altText || variant.title,
      }
    }
    const percentDiscountRemain = (100 - discountPercentage) / 100
    if (variant.price) {
      variant.price = percentDiscountRemain?.toFixed(2)
    }
    if (variant.compareAtPrice) {
      variant.compareAtPrice = (
        variant.compareAtPrice * percentDiscountRemain
      )?.toFixed(2)
    }
    if (variant.priceV2?.amount && !variant.price) {
      variant.priceV2.amount = (
        variant.priceV2.amount * percentDiscountRemain
      )?.toFixed(2)
    }
    if (variant.compareAtPriceV2?.amount && !variant.compareAtPrice) {
      variant.compareAtPriceV2.amount = (
        variant.compareAtPriceV2.amount * percentDiscountRemain
      )?.toFixed(2)
    }

    if (variant.priceV2 && !variant.price) {
      variant.price = (+variant.priceV2.amount)?.toFixed(2) || 0
    }
    if (variant.compareAtPriceV2 && !variant.compareAtPrice) {
      variant.compareAtPrice =
        (+variant.compareAtPriceV2.amount)?.toFixed(2) || 0
    }

    return variant
  }

  transformProduct(product: any): Product {
    if (!product) return product
    if (product.images) {
      product.images = product.images.map((image: any) => {
        return Object.assign({}, image, {
          src: image.url,
          alt: image.altText || product.title,
        })
      })
    }
    if (product.featuredImage) {
      product.featuredImage = Object.assign({}, product.featuredImage, {
        src: product.featuredImage.url,
        alt: product.featuredImage.altText || product.title,
      })
    }

    if (product.variants) {
      const discountDefaultTag = product.tags?.find((tag) =>
        tag.includes(PRODUCT_TAG_DISCOUNT_DEFAULT)
      )
      const valueDiscount = discountDefaultTag?.split('_').at(-1)
      product.variants = product.variants.map((variant: any) => {
        if (discountDefaultTag && valueDiscount) {
          return this.transformProductVariantDiscountDefault(
            variant,
            valueDiscount / 10
          )
        } else {
          return this.transformProductVariant(variant)
        }
      })
    }

    return product
  }

  transformProducts(products: any[]): Product[] {
    if (!products.length) return products
    products.map((product: any) => this.transformProduct(product))
    return products
  }

  transformCollection(collection: any): NullOrType<Collection> {
    if (!collection) return null
    if (collection.products) {
      collection.products = collection.products.map((product: any) => {
        return this.transformProduct(product)
      })
    }
    return collection
  }

  transformCart(cart: any): Cart {
    if (!cart) return cart
    if (cart.lineItems) {
      cart.lineItems = cart.lineItems.map((lineItem: any) => {
        if (lineItem.merchandise) {
          lineItem.merchandise = this.transformProductVariant(
            lineItem.merchandise
          )
        }
        return lineItem
      })
    }

    // Replace the part between "https://" and "/cart"
    if (cart.checkoutUrl) {
      cart.checkoutUrl = cart.checkoutUrl.replace(
        /https:\/\/.*?(?=\/cart)/,
        `https://${SHOP_CHECKOUT_DOMAIN}`
      )
    }
    return cart
  }

  transformCheckout(checkout: any): Checkout {
    if (!checkout) return checkout
    if (checkout.lineItems) {
      checkout.lineItems = checkout.lineItems.map((lineItem: any) => {
        lineItem.variant = this.transformProductVariant(lineItem.variant)
        return lineItem
      })
    }
    if (checkout.webUrl) {
      checkout.webUrl = checkout.webUrl.replace(
        SHOP_DOMAIN,
        SHOP_CHECKOUT_DOMAIN
      )
    }
    return checkout
  }

  transformProductFilterParam(filters: any): ProductFilter[] {
    const allowFilters = ['productType']
    // only allow some filter
    if (filters) {
      filters = filters.map((filter: any) => {
        const newFilter: ProductFilter = {}
        for (const key in filter) {
          if (Object.prototype.hasOwnProperty.call(filter, key)) {
            const element = filter[key as keyof ProductFilter]
            if (allowFilters.includes(key)) {
              newFilter[key as keyof ProductFilter] = element
            }
          }
        }
        return newFilter
      })
    }
    return filters
  }
}
